"use client";

import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "framer-motion";

import { NormalText } from "@/components/text/NormalText";
import type { ModalTheme } from "@/components/types";
import { cn } from "@/utils/cn";

// Reusable, presentational sort dropdown. Holds no store or feature knowledge — the caller passes the current value, the options to show, and an onChange.
export function SortDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  listClassName,
  className,
  theme = "light",
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (nextValue: T) => void;
  // Anchoring classes for the options list (e.g. "left-0 origin-top-left"). The list is wider than the trigger and must open toward the free space; which side that is depends on the caller's layout per breakpoint, so the call site owns it.
  listClassName: string;
  className: string;
  // Dark: matches the TTV dialogs' neutral panel instead of a white control glowing in the middle of it (same switch as SearchInput).
  theme?: ModalTheme;
}) {
  const isDark = theme === "dark";

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className={className}>
      <NormalText elementType="p" className="mb-1 text-gray-400">
        {label}
      </NormalText>
      <Menu as="div" className="relative inline-block text-left">
        {({ open }) => (
          <>
            <Menu.Button
              className={cn(
                "flex items-center gap-1.5 rounded-3xl border py-1 pl-3 pr-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                isDark ?
                  "border-transparent bg-ttvRaised hover:bg-ttvArmed focus-visible:ring-offset-ttvCard"
                : "border-gray-400 bg-white"
              )}
            >
              <NormalText
                elementType="span"
                className={isDark ? "text-gray-100" : "text-gray-900"}
              >
                {selectedOption?.label}
              </NormalText>
              <ChevronDownIcon
                className={cn(
                  "h-3 w-3 shrink-0",
                  isDark ? "text-gray-400" : "text-gray-500"
                )}
                aria-hidden
              />
            </Menu.Button>

            <AnimatePresence>
              {open && (
                <Menu.Items
                  as={motion.div}
                  static
                  className={cn(
                    "absolute z-50 mt-1.5 w-36 space-y-0.5 rounded-lg border p-0.5 shadow-lg ring-1 ring-black/5 focus:outline-none",
                    isDark ?
                      "border-transparent bg-ttvRaised shadow-floatingCard"
                    : "border-gray-100 bg-white",
                    listClassName
                  )}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.1, ease: "easeOut" }}
                >
                  {options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onChange(option.value)}
                            className={cn(
                              "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors",
                              active &&
                                (isDark ? "bg-ttvArmed" : "bg-gray-100"),
                              isSelected &&
                                (isDark ?
                                  "bg-ttvLine font-medium text-white"
                                : "bg-primaryLight font-medium text-primary"),
                              !isSelected &&
                                (isDark ? "text-gray-300" : "text-gray-700")
                            )}
                          >
                            <span className="truncate">{option.label}</span>
                          </button>
                        )}
                      </Menu.Item>
                    );
                  })}
                </Menu.Items>
              )}
            </AnimatePresence>
          </>
        )}
      </Menu>
    </div>
  );
}
